/**
 * Authentication Helper Functions
 * 
 * This module provides utility functions for handling JWT tokens, base64 decoding,
 * and token validation. It's designed to be used by the AuthenticationService
 * and other components that need to work with authentication tokens.
 * 
 * Key Features:
 * - Safe base64 decoding with automatic padding
 * - JWT token parsing and validation
 * - Token expiration checking
 * - URL-safe base64 character handling
 */

/**
 * Safely decodes a base64 string with automatic padding and URL-safe character support
 * This function handles JWT tokens that may be missing padding or use URL-safe characters
 * 
 * @param {string} str - The base64 string to decode (e.g., JWT payload part)
 * @returns {string|null} - The decoded string, or null if decoding fails
 * 
 * How it works:
 * 1. Adds padding if needed (base64 strings must have length divisible by 4)
 * 2. Converts URL-safe characters back to standard base64
 * 3. Uses atob() to decode the base64 string
 * 
 * Base64 padding rules:
 * - 1 byte (8 bits) → 2 base64 chars + 2 padding chars (==)
 * - 2 bytes (16 bits) → 3 base64 chars + 1 padding char (=)
 * - 3 bytes (24 bits) → 4 base64 chars (no padding needed)
 * 
 * URL-safe base64 characters:
 * - '-' becomes '+' (minus to plus)
 * - '_' becomes '/' (underscore to slash)
 * 
 * @example
 * // Decode a JWT payload part
 * const payload = safeBase64Decode('eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE3MzU2ODgwMDB9');
 * // Returns: '{"sub":"1234567890","name":"John Doe","iat":1516239022,"exp":1735688000}'
 */
export function safeBase64Decode(str) {
  try {
    // Step 1: Add padding if needed for base64
    // Base64 strings must have length divisible by 4
    // If not, we add '=' characters to make it divisible by 4
    let paddedStr = str;
    while (paddedStr.length % 4 !== 0) {
      paddedStr += '=';
    }
    
    // Step 2: Replace URL-safe characters with standard base64 characters
    // JWT tokens often use URL-safe base64 to avoid special characters in URLs
    // '-' (minus) becomes '+' (plus) - used in URL-safe base64
    // '_' (underscore) becomes '/' (slash) - used in URL-safe base64
    paddedStr = paddedStr.replace(/-/g, '+').replace(/_/g, '/');
    
    // Step 3: Decode the base64 string using browser's built-in atob function
    // atob() expects standard base64 with proper padding
    return atob(paddedStr);
  } catch (error) {
    // If decoding fails, log a warning and return null
    // This prevents the application from crashing when encountering malformed tokens
    console.warn('Failed to decode base64 string:', error);
    return null;
  }
}

/**
 * Validates if a string is a valid JWT token format
 * 
 * @param {string} token - The token string to validate
 * @returns {boolean} - true if token has valid JWT format, false otherwise
 * 
 * JWT format requirements:
 * - Must be a non-empty string
 * - Must contain exactly 3 parts separated by dots
 * - Each part must not be empty
 * 
 * @example
 * isValidJWTFormat('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE3MzU2ODgwMDB9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c');
 * // Returns: true
 */
export function isValidJWTFormat(token) {
  // Check if token exists, is a string, and is not empty
  if (!token || typeof token !== 'string' || token.trim() === '') {
    return false;
  }

  // Split JWT token into its three parts
  const parts = token.split('.');
  
  // JWT tokens must have exactly 3 parts separated by dots
  if (parts.length !== 3) {
    return false;
  }

  // Validate that each part is not empty
  // Empty parts indicate malformed JWT tokens
  if (!parts[0] || !parts[1] || !parts[2]) {
    return false;
  }

  return true;
}

/**
 * Extracts the expiration time from a JWT token
 * This function safely parses JWT tokens and extracts the 'exp' claim
 * 
 * @param {string} token - The JWT token to parse
 * @returns {Date|null} - The expiration date, or null if parsing fails
 * 
 * JWT Token Structure:
 * header.payload.signature
 * 
 * The payload (middle part) contains the claims in base64-encoded JSON format
 * We need to decode this payload to access the 'exp' (expiration) claim
 * 
 * Error Handling:
 * - Returns null for invalid/malformed tokens instead of throwing errors
 * - Logs detailed warnings for debugging purposes
 * - Gracefully handles edge cases to prevent application crashes
 * 
 * @example
 * const expiryTime = getTokenExpiryTime('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE3MzU2ODgwMDB9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c');
 * // Returns: Date object representing the expiration time
 */
export function getTokenExpiryTime(token) {
  try {
    // Step 1: Validate JWT format
    if (!isValidJWTFormat(token)) {
      console.warn('Invalid JWT token format');
      return null;
    }

    // Step 2: Split JWT token into its three parts
    const parts = token.split('.');
    
    // Step 3: Decode and parse the payload (middle part)
    try {
      // Use our safe base64 decode function to handle padding and URL-safe characters
      const decodedPayload = safeBase64Decode(parts[1]);
      if (!decodedPayload) {
        console.warn('Failed to decode token payload');
        return null;
      }
      
      // Parse the decoded payload as JSON to access the claims
      const payload = JSON.parse(decodedPayload);
      
      // Step 4: Extract the expiration time (exp claim)
      // The 'exp' claim contains the token expiration timestamp in seconds since epoch
      if (!payload.exp) {
        console.warn('Token payload does not contain expiration time');
        return null;
      }
      
      // Step 5: Validate that exp is a number
      // JWT spec requires exp to be a numeric timestamp
      if (typeof payload.exp !== 'number') {
        console.warn('Token expiration time is not a number');
        return null;
      }
      
      // Step 6: Convert expiration time to Date object
      // exp is in seconds since epoch, convert to milliseconds for Date constructor
      return new Date(payload.exp * 1000);
    } catch (base64Error) {
      // Handle errors specific to base64 decoding or JSON parsing
      console.warn('Failed to decode token payload as base64:', base64Error);
      return null;
    }
  } catch (error) {
    // Handle any other unexpected errors during token parsing
    console.error('Error parsing token expiry time:', error);
    return null;
  }
}

/**
 * Checks if a JWT token is expired
 * 
 * @param {string} token - The JWT token to check
 * @param {number} [bufferMs=0] - Buffer time in milliseconds before considering token expired
 * @returns {boolean|null} - true if expired, false if valid, null if can't determine
 * 
 * @example
 * const isExpired = isTokenExpired(token, 60000); // Check if expires within 1 minute
 * // Returns: true if token expires within 1 minute, false otherwise
 */
export function isTokenExpired(token, bufferMs = 0) {
  const expiryTime = getTokenExpiryTime(token);
  if (!expiryTime) {
    return null; // Can't determine
  }

  const currentTime = new Date();
  const timeDifference = expiryTime - currentTime;
  
  return timeDifference < bufferMs;
}

/**
 * Checks if a JWT token needs to be refreshed
 * 
 * @param {string} token - The JWT token to check
 * @param {number} [preRefreshTime=60000] - Time in milliseconds before expiry to consider refresh needed
 * @returns {boolean|null} - true if refresh needed, false if not needed, null if can't determine
 * 
 * @example
 * const needsRefresh = needsTokenRefresh(token, 60000); // Check if needs refresh within 1 minute
 * // Returns: true if token expires within 1 minute, false otherwise
 */
export function needsTokenRefresh(token, preRefreshTime = 60000) {
  const expiryTime = getTokenExpiryTime(token);
  if (!expiryTime) {
    return null; // Can't determine
  }

  const currentTime = new Date();
  const timeDifference = expiryTime - currentTime;
  
  return timeDifference < preRefreshTime;
}

/**
 * Extracts all claims from a JWT token payload
 * 
 * @param {string} token - The JWT token to parse
 * @returns {object|null} - The decoded claims object, or null if parsing fails
 * 
 * @example
 * const claims = getTokenClaims(token);
 * // Returns: { sub: "1234567890", name: "John Doe", iat: 1516239022, exp: 1735688000 }
 */
export function getTokenClaims(token) {
  try {
    if (!isValidJWTFormat(token)) {
      return null;
    }

    const parts = token.split('.');
    const decodedPayload = safeBase64Decode(parts[1]);
    
    if (!decodedPayload) {
      return null;
    }

    return JSON.parse(decodedPayload);
  } catch (error) {
    console.warn('Failed to extract token claims:', error);
    return null;
  }
}

/**
 * Creates a mock JWT token for testing purposes
 * 
 * @param {object} claims - The claims to include in the token
 * @param {number} [expiryOffset=3600] - Expiry time offset in seconds from now
 * @returns {string} - A mock JWT token string
 * 
 * @example
 * const mockToken = createMockToken({ sub: "123", name: "Test User" }, 7200);
 * // Returns: A mock JWT token that expires in 2 hours
 */
export function createMockToken(claims = {}, expiryOffset = 3600) {
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };

  const payload = {
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + expiryOffset,
    ...claims
  };

  // Simple base64 encoding (not cryptographically secure)
  const encodedHeader = btoa(JSON.stringify(header));
  const encodedPayload = btoa(JSON.stringify(payload));
  const signature = 'mock_signature_for_testing';

  return `${encodedHeader}.${encodedPayload}.${signature}`;
}

/**
 * Validates if a token has required claims
 * 
 * @param {string} token - The JWT token to validate
 * @param {string[]} requiredClaims - Array of claim names that must be present
 * @returns {boolean} - true if all required claims are present, false otherwise
 * 
 * @example
 * const hasRequiredClaims = validateRequiredClaims(token, ['sub', 'exp', 'iat']);
 * // Returns: true if token has all required claims, false otherwise
 */
export function validateRequiredClaims(token, requiredClaims) {
  const claims = getTokenClaims(token);
  if (!claims) {
    return false;
  }

  return requiredClaims.every(claim => claims.hasOwnProperty(claim));
}

// Export all functions as default object for convenience
export default {
  safeBase64Decode,
  isValidJWTFormat,
  getTokenExpiryTime,
  isTokenExpired,
  needsTokenRefresh,
  getTokenClaims,
  createMockToken,
  validateRequiredClaims
};
